import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  LinearProgress,
} from "@mui/material";
import { useNotify } from "react-admin";
import { baseUrl } from "../../customDataProvider";

interface CreateTenantDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateTenantDialog = ({
  open,
  onClose,
  onSuccess,
}: CreateTenantDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  // Form fields
  const [tenantCode, setTenantCode] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [description, setDescription] = useState("");

  const notify = useNotify();

  const resetForm = () => {
    setTenantCode("");
    setTenantName("");
    setDescription("");
    setError(null);
    setResult(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validate = (): boolean => {
    if (!tenantCode.trim()) {
      setError("Tenant code is required");
      return false;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(tenantCode)) {
      setError(
        "Tenant code can only contain letters, numbers, hyphens, and underscores"
      );
      return false;
    }
    if (!tenantName.trim()) {
      setError("Tenant name is required");
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${baseUrl}/tenants/provision`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          tenantCode: tenantCode.trim(),
          tenantName: tenantName.trim(),
          description: description.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message || `Failed to create tenant: ${response.statusText}`
        );
      }

      const data = await response.json();
      setResult(data);
      notify("Tenant created successfully", { type: "success" });
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || "An error occurred while creating the tenant");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {result ? "Tenant Created" : "Create New Tenant"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Tenant "{result?.tenant?.name}" has been created successfully!
            </Alert>
            <Box
              sx={{
                bgcolor: "grey.50",
                p: 2,
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Tenant Code
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {result?.tenant?.code}
                </Typography>
              </Box>
              {result?.adminUser && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Admin User
                  </Typography>
                  <Typography variant="body1">
                    {result?.adminUser?.firstName} {result?.adminUser?.lastName} (
                    {result?.adminUser?.email})
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Forms Configured
                </Typography>
                <Typography variant="body1">
                  {result?.formsConfigured} forms
                </Typography>
              </Box>
              {result?.isNewUser && result?.defaultPassword && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Default Password
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="warning.main">
                    {result.defaultPassword}
                  </Typography>
                </Box>
              )}
              {result?.isNewUser && result?.passwordWasCustom && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Password
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="success.main">
                    Custom password was set successfully
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Tenant Details
            </Typography>
            <TextField
              label="Tenant Code"
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value.toLowerCase())}
              helperText="Unique identifier (e.g., 'acme-corp'). Letters, numbers, hyphens, underscores only."
              required
              fullWidth
              autoFocus
              disabled={loading}
            />
            <TextField
              label="Tenant Name"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              helperText="Display name for the tenant (e.g., 'Acme Corporation')"
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText="Optional description"
              multiline
              rows={2}
              fullWidth
              disabled={loading}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {result ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              Create Tenant
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
